import type { DiagramPayload } from './service-diagram.js';
import { panelNavHtml } from '../panel-nav-html.js';

export interface ExampleBuildItem {
  id: string;
  name: string;
  summary: string;
  diagram: DiagramPayload | null;
}

export interface ShowExampleBuildsPayload {
  highlight: string | null;
  items: ExampleBuildItem[];
}

/**
 * Overlay listing illustrative example builds. Each card is clickable —
 * clicking fires a merlin:show-diagram CustomEvent so the caller can
 * open the flow diagram inline without a model tool call.
 */
export class ExampleBuildsOverlay {
  private readonly root: HTMLElement;
  private cachedDiagrams: Map<string, DiagramPayload> = new Map();

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('services-overlay');
    this.root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t === this.root || t.closest('[data-action="close"]')) {
        this.hide();
        document.dispatchEvent(new CustomEvent('merlin:nav-close', { bubbles: true }));
        return;
      }
      if (t.closest('[data-action="back"]')) {
        document.dispatchEvent(new CustomEvent('merlin:nav-back', { bubbles: true }));
        return;
      }
      const card = t.closest<HTMLElement>('[data-build-id]');
      if (card) {
        const buildId = card.dataset.buildId;
        if (buildId && this.cachedDiagrams.has(buildId)) {
          document.dispatchEvent(
            new CustomEvent('merlin:show-diagram', {
              detail: this.cachedDiagrams.get(buildId),
            }),
          );
        }
      }
    });
  }

  show(payload: ShowExampleBuildsPayload) {
    const { items, highlight } = payload;

    this.cachedDiagrams = new Map(
      items
        .filter((item) => item.diagram !== null)
        .map((item) => [item.id, item.diagram as DiagramPayload]),
    );

    const rows = items
      .map(
        (b) => `
          <li class="service-row ${highlight === b.id ? 'highlight' : ''} ${b.diagram ? 'has-diagram' : ''}"
              data-build-id="${escapeHtml(b.id)}"
              ${b.diagram ? 'role="button" tabindex="0" aria-label="View diagram for ' + escapeHtml(b.name) + '"' : ''}>
            <div class="service-name">${escapeHtml(b.name)}</div>
            <div class="service-summary">${escapeHtml(b.summary)}</div>
            ${b.diagram ? '<div class="service-diagram-hint">View flow →</div>' : ''}
          </li>`,
      )
      .join('');

    this.root.innerHTML = `
      <div class="services-sheet">
        ${panelNavHtml()}
        <div class="services-header">
          <div>
            <div class="services-title">Example builds</div>
            <div class="services-sub">Every engagement is custom-scoped.</div>
          </div>
        </div>
        <ul class="services-list">${rows}</ul>
      </div>
    `;
    this.root.classList.add('visible');

    // Keyboard accessibility for diagram cards
    this.root.querySelectorAll<HTMLElement>('[data-build-id][role="button"]').forEach((el) => {
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          el.click();
        }
      });
    });
  }

  hide() {
    this.root.classList.remove('visible');
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
